type GeocodeResult = {
  latitude: number;
  longitude: number;
  components: google.maps.GeocoderAddressComponent[];
  formattedAddress: string;
} | null;

export const geocodeAddress = (value: string): Promise<GeocodeResult> => {
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: value }, (results, status) => {
      const r = results?.[0];
      if (status === "OK" && r) {
        const loc = r.geometry.location;
        resolve({
          latitude: loc.lat(),
          longitude: loc.lng(),
          components: r.address_components ?? [],
          formattedAddress: r.formatted_address ?? value,
        });
      } else {
        resolve(null);
      }
    });
  });
};

export type ParsedAddress = {
  /**
   * The street line only — "193 Glengarry Road", "Apartment 12B, 148 Marine
   * Parade" — built from Google's own components rather than by splitting the
   * formatted string on commas.
   *
   * This is what `Address.address1` should hold. The full courier-ready address
   * is composed back up from the components server-side, so nothing is lost by
   * storing the street line here.
   */
  street: string;
  city: string;
  countryId: string | null;
  postalCode: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
};

const pickAddressComponent = (
  comps: google.maps.GeocoderAddressComponent[],
  type: string,
  mode: "long" | "short" = "long",
) => {
  const c = comps.find((x) => x.types.includes(type));
  return mode === "short" ? c?.short_name : c?.long_name;
};

export type PlaceDetails = {
  formattedAddress: string;
  latitude: number;
  longitude: number;
  addressComponents: google.maps.places.AddressComponent[];
};

/**
 * Compose the street line from Google's address components.
 *
 * `subpremise` is the unit/apartment and belongs in front of the street, because
 * without it the address names the wrong door. `street_number` + `route` is the
 * street itself. Anything else — locality, region, postcode, country — is a
 * separate component and deliberately not included.
 */
const composeStreet = (
  pick: (type: string, mode?: "long" | "short") => string | null | undefined,
): string => {
  const subpremise = pick("subpremise");
  const streetNumber = pick("street_number");
  const route = pick("route");

  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  if (!street) return "";
  return subpremise ? `${subpremise}/${street}` : street;
};

export const parsePlaceAddressComponents = (
  details: PlaceDetails,
): ParsedAddress => {
  const comps = details.addressComponents;

  const pick = (type: string, mode: "long" | "short" = "long") => {
    const c = comps.find((x) => x.types.includes(type));
    return mode === "short" ? c?.shortText : c?.longText;
  };

  const city =
    pick("locality") ||
    pick("postal_town") ||
    pick("administrative_area_level_2") ||
    "";

  const state = pick("administrative_area_level_1") || "";
  const postalCode = pick("postal_code") || "";

  return {
    street: composeStreet(pick),
    city,
    state,
    postalCode,
    countryId: null,
    latitude: details.latitude ?? null,
    longitude: details.longitude ?? null,
  };
};

export const parseGeocodeComponents = (
  result: NonNullable<GeocodeResult>,
): ParsedAddress => {
  const comps = result.components;

  const city =
    pickAddressComponent(comps, "locality") ||
    pickAddressComponent(comps, "postal_town") ||
    pickAddressComponent(comps, "administrative_area_level_2") ||
    "";

  const state =
    pickAddressComponent(comps, "administrative_area_level_1") || "";
  const postalCode = pickAddressComponent(comps, "postal_code") || "";

  return {
    street: composeStreet((type, mode) =>
      pickAddressComponent(comps, type, mode),
    ),
    city,
    state,
    postalCode,
    countryId: null,
    latitude: result.latitude ?? null,
    longitude: result.longitude ?? null,
  };
};

export const distanceInMetersBetween = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
) => {
  if (
    typeof google !== "undefined" &&
    google.maps &&
    google.maps.geometry &&
    google.maps.geometry.spherical &&
    typeof google.maps.geometry.spherical.computeDistanceBetween === "function"
  ) {
    const point1 = new google.maps.LatLng(lat1, lng1);
    const point2 = new google.maps.LatLng(lat2, lng2);

    const distanceInMeters =
      google.maps.geometry.spherical.computeDistanceBetween(point1, point2);
    return distanceInMeters;
  }

  // fallback — calculation using the haversine formula
  const toRadians = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371e3;
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lng2 - lng1);

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const metersToLabel = (meters: number) => {
  if (meters < 1000) {
    return `${meters.toFixed()} m`;
  }

  return `${(meters / 1000).toFixed(1)} km`;
};
